import { apiClient, toQueryParams } from './client';
import type {
  Notification,
  NotificationSetting,
  NotificationSettingInput,
  PagedResponse,
  PaginationParams,
} from '@/types';

/** Raw shape returned by the backend (NotificationDto). */
interface NotificationDto {
  notificationId: number;
  emailId: number;
  message: string | null;
  isRead: boolean;
  isSent: boolean;
  sentDate: string | null;
  createdDate: string;
}

interface NotificationDtoPagedResult {
  items: NotificationDto[] | null;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

function toNotification(dto: NotificationDto): Notification {
  return {
    id: dto.notificationId,
    emailId: dto.emailId,
    message: dto.message ?? '',
    isRead: dto.isRead,
    isSent: dto.isSent,
    sentDate: dto.sentDate ?? undefined,
    createdAt: dto.createdDate,
  };
}

function toPagedNotifications(data: NotificationDtoPagedResult): PagedResponse<Notification> {
  return {
    items: (data.items ?? []).map(toNotification),
    page: data.page,
    pageSize: data.pageSize,
    totalCount: data.totalCount,
    totalPages: data.totalPages,
  };
}

/** Raw shape returned by the backend (NotificationSettingDto). */
interface NotificationSettingDto {
  settingId: number;
  brandId: number | null;
  brandName: string | null;
  businessUnitId: number | null;
  businessUnitName: string | null;
  thresholdType: string | null;
  thresholdValue: number;
  notificationChannel: string | null;
  isActive: boolean;
}

interface NotificationSettingDtoPagedResult {
  items: NotificationSettingDto[] | null;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

function toNotificationSetting(dto: NotificationSettingDto): NotificationSetting {
  return {
    id: dto.settingId,
    brandId: dto.brandId ?? undefined,
    brandName: dto.brandName ?? undefined,
    businessUnitId: dto.businessUnitId ?? undefined,
    businessUnitName: dto.businessUnitName ?? undefined,
    thresholdType: (dto.thresholdType ?? 'CompositeScore') as NotificationSetting['thresholdType'],
    thresholdValue: dto.thresholdValue,
    notificationChannel: (dto.notificationChannel ?? 'InApp') as NotificationSetting['notificationChannel'],
    isActive: dto.isActive,
  };
}

function toPagedSettings(data: NotificationSettingDtoPagedResult): PagedResponse<NotificationSetting> {
  return {
    items: (data.items ?? []).map(toNotificationSetting),
    page: data.page,
    pageSize: data.pageSize,
    totalCount: data.totalCount,
    totalPages: data.totalPages,
  };
}

export const notificationsApi = {
  list: async (
    params: PaginationParams & { unreadOnly?: boolean } = {},
  ): Promise<PagedResponse<Notification>> => {
    const { data } = await apiClient.get<NotificationDtoPagedResult>('/notifications', {
      params: toQueryParams({ page: 1, pageSize: 50, ...params }),
    });
    return toPagedNotifications(data);
  },
  /** The backend has no dedicated unread-count endpoint — derive it from the paged total. */
  getUnreadCount: async (): Promise<number> => {
    const { data } = await apiClient.get<NotificationDtoPagedResult>('/notifications', {
      params: toQueryParams({ unreadOnly: true, page: 1, pageSize: 1 }),
    });
    return data.totalCount;
  },
  markRead: async (id: number): Promise<void> => {
    await apiClient.put(`/notifications/${id}/read`);
  },
  /** No bulk endpoint exists — fetch unread ids and mark each one individually. */
  markAllRead: async (): Promise<void> => {
    const { data } = await apiClient.get<NotificationDtoPagedResult>('/notifications', {
      params: toQueryParams({ unreadOnly: true, page: 1, pageSize: 1000 }),
    });
    const unreadIds = (data.items ?? []).map((n) => n.notificationId);
    await Promise.all(unreadIds.map((id) => apiClient.put(`/notifications/${id}/read`)));
  },
  /**
   * Notification settings are a collection scoped to an optional brand/business unit —
   * there is no singleton settings object on the backend.
   */
  listSettings: async (
    params: PaginationParams = {},
  ): Promise<PagedResponse<NotificationSetting>> => {
    const { data } = await apiClient.get<NotificationSettingDtoPagedResult>(
      '/notifications/settings',
      { params: toQueryParams({ page: 1, pageSize: 50, ...params }) },
    );
    return toPagedSettings(data);
  },
  createSettings: async (input: NotificationSettingInput): Promise<NotificationSetting> => {
    const { data } = await apiClient.post<NotificationSettingDto>('/notifications/settings', {
      brandId: input.brandId,
      businessUnitId: input.businessUnitId,
      thresholdType: input.thresholdType,
      thresholdValue: input.thresholdValue,
      notificationChannel: input.notificationChannel,
    });
    return toNotificationSetting(data);
  },
  updateSettings: async (
    id: number,
    input: NotificationSettingInput,
  ): Promise<NotificationSetting> => {
    const { data } = await apiClient.put<NotificationSettingDto>(
      `/notifications/settings/${id}`,
      {
        brandId: input.brandId,
        businessUnitId: input.businessUnitId,
        thresholdType: input.thresholdType,
        thresholdValue: input.thresholdValue,
        notificationChannel: input.notificationChannel,
        isActive: input.isActive ?? true,
      },
    );
    return toNotificationSetting(data);
  },
  deleteSettings: async (id: number): Promise<void> => {
    await apiClient.delete(`/notifications/settings/${id}`);
  },
};
